import React from 'react';
import PropTypes from 'prop-types';
import { FaMapMarkerAlt, FaCalendarAlt, FaShieldAlt, FaGlobe } from 'react-icons/fa';
import IdentityTags from './IdentityTags';
import { useUser } from '../../contexts/UserContext';
import { 
  shouldDisplayIdentity, 
  getVisibleIdentity, 
  getDisplayName, 
  getDisplayAvatar 
} from '../../utils/identityHelpers';
import '../../styles/ProfileCard.css';

/**
 * Component for displaying a user profile card with proper visibility controls
 */
const ProfileCard = ({ 
  user, 
  variant = 'standard', 
  onConnect,
  onMessage,
  onViewProfile,
  showActions = true
}) => {
  const { currentUser } = useUser();
  
  if (!user) return null;
  
  // Get display name and avatar based on anonymity settings
  const displayName = getDisplayName(user);
  const displayAvatar = getDisplayAvatar(user);
  
  // Check which identity fields should be visible
  const showGender = shouldDisplayIdentity(user, 'genderIdentity', currentUser);
  const showOrientation = shouldDisplayIdentity(user, 'sexualOrientation', currentUser);
  const showPronouns = shouldDisplayIdentity(user, 'pronouns', currentUser);
  
  // Get visible identity values
  const visibleGender = getVisibleIdentity(user, 'genderIdentity', currentUser);
  const visibleOrientation = getVisibleIdentity(user, 'sexualOrientation', currentUser);
  const visiblePronouns = getVisibleIdentity(user, 'pronouns', currentUser);
  
  // Handle action buttons
  const handleConnect = (e) => {
    e.stopPropagation();
    if (onConnect) onConnect(user.id);
  };
  
  const handleMessage = (e) => {
    e.stopPropagation();
    if (onMessage) onMessage(user.id);
  };
  
  const handleViewProfile = () => {
    if (onViewProfile) onViewProfile(user.id);
  };
  
  // Determine if this is the current user's profile
  const isCurrentUser = currentUser && currentUser.id === user.id;
  
  // Render different card variants
  const renderCardContent = () => {
    switch (variant) {
      case 'compact':
        return (
          <>
            <div className="profile-card__avatar-container">
              <img 
                src={displayAvatar} 
                alt={`${displayName}'s avatar`} 
                className="profile-card__avatar profile-card__avatar--small" 
              />
              {user.isLocalQueer && (
                <span className="profile-card__badge profile-card__badge--local" title="Local Queer">LQ</span>
              )}
            </div>
            <div className="profile-card__content">
              <h3 className="profile-card__name">{displayName}</h3>
              <p className="profile-card__location">
                <FaMapMarkerAlt className="profile-card__icon" />
                {user.location}
              </p>
            </div>
          </>
        );
        
      case 'connection':
        return (
          <>
            <div className="profile-card__avatar-container">
              <img 
                src={displayAvatar} 
                alt={`${displayName}'s avatar`} 
                className="profile-card__avatar" 
              />
              {user.isLocalQueer && (
                <span className="profile-card__badge profile-card__badge--local" title="Local Queer">LQ</span>
              )}
              {user.isSafetyVerified && (
                <span className="profile-card__badge profile-card__badge--verified" title="Safety Verified">
                  <FaShieldAlt />
                </span>
              )}
            </div>
            <div className="profile-card__content">
              <h3 className="profile-card__name">{displayName}</h3>
              <p className="profile-card__location">
                <FaMapMarkerAlt className="profile-card__icon" />
                {user.location}
              </p>
              {showPronouns && visiblePronouns && (
                <p className="profile-card__pronouns">{visiblePronouns}</p>
              )}
              {user.bio && (
                <p className="profile-card__bio">{user.bio.substring(0, 100)}{user.bio.length > 100 ? '...' : ''}</p>
              )}
            </div>
          </>
        );
        
      case 'standard':
      default:
        return (
          <>
            <div className="profile-card__avatar-container">
              <img 
                src={displayAvatar} 
                alt={`${displayName}'s avatar`} 
                className="profile-card__avatar" 
              />
              {user.isLocalQueer && (
                <span className="profile-card__badge profile-card__badge--local" title="Local Queer">LQ</span>
              )}
              {user.isSafetyVerified && (
                <span className="profile-card__badge profile-card__badge--verified" title="Safety Verified">
                  <FaShieldAlt />
                </span>
              )}
            </div>
            <div className="profile-card__content">
              <h3 className="profile-card__name">{displayName}</h3>
              <p className="profile-card__location">
                <FaMapMarkerAlt className="profile-card__icon" />
                {user.location}
              </p>
              {showPronouns && visiblePronouns && (
                <p className="profile-card__pronouns">{visiblePronouns}</p>
              )}
              <div className="profile-card__details">
                {user.languagesSpoken && (
                  <p className="profile-card__detail">
                    <FaGlobe className="profile-card__icon" />
                    {user.languagesSpoken.join(', ')}
                  </p>
                )}
              </div>
              
              {/* Identity tags */}
              {showGender && visibleGender && (
                <div className="profile-card__identity-section">
                  <IdentityTags tags={visibleGender} type="gender" size="small" />
                </div>
              )}
              
              {showOrientation && visibleOrientation && (
                <div className="profile-card__identity-section">
                  <IdentityTags tags={visibleOrientation} type="orientation" size="small" />
                </div>
              )}
              
              {user.travelStyle && (
                <div className="profile-card__identity-section">
                  <IdentityTags tags={user.travelStyle} type="travel" size="small" />
                </div>
              )}
              
              {user.bio && (
                <p className="profile-card__bio">{user.bio.substring(0, 150)}{user.bio.length > 150 ? '...' : ''}</p>
              )}
            </div>
          </>
        );
    }
  };
  
  return (
    <div 
      className={`profile-card profile-card--${variant}`}
      onClick={handleViewProfile}
    >
      {renderCardContent()}
      
      {showActions && !isCurrentUser && (
        <div className="profile-card__actions">
          <button 
            className="profile-card__action-btn profile-card__action-btn--connect"
            onClick={handleConnect}
            aria-label="Connect with user"
          >
            Connect
          </button>
          <button 
            className="profile-card__action-btn profile-card__action-btn--message"
            onClick={handleMessage}
            aria-label="Message user"
          >
            Message
          </button>
        </div>
      )}
      
      {isCurrentUser && (
        <div className="profile-card__current-user-indicator">
          This is you
        </div>
      )}
    </div>
  );
};

ProfileCard.propTypes = {
  user: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['standard', 'compact', 'connection']),
  onConnect: PropTypes.func,
  onMessage: PropTypes.func,
  onViewProfile: PropTypes.func,
  showActions: PropTypes.bool
};

export default ProfileCard;
